import { useState, useEffect, useRef, useCallback } from 'react';
import { playSessionEndChime, unlockAudio } from '../audio';
import { tapHaptic } from '../native';

// The optional session timer: a wall-clock countdown that ends with a chime +
// haptics, then pauses tapping (and the stats clock) until the user taps the
// "session complete" prompt to carry on.
//
// - `settingsRef`: always-current settings, so the end chime uses live audio prefs.
// - `paused`: true while the settings panel / a modal is open — the countdown
//   holds and resumes from where it left off.
// - `onSessionEnd`: called at zero (the caller banks its stats clock).
// - `onResume`: called when the user taps to continue after a session.
const useSessionTimer = ({ settingsRef, paused, onSessionEnd, onResume }) => {
  // Countdown in seconds (null = no active session) and a brief "complete"
  // flag for the end-of-session message.
  const [secondsLeft, setSecondsLeft] = useState(null);
  // After a timed session ends, tapping is paused until the user resumes.
  const [awaitingResume, setAwaitingResume] = useState(false);
  const intervalRef = useRef(null);
  // Mirror secondsLeft into a ref so the pause/resume effect can read the
  // latest value without re-subscribing every tick.
  const secondsLeftRef = useRef(null);
  useEffect(() => { secondsLeftRef.current = secondsLeft; }, [secondsLeft]);

  const clearTick = () => {
    if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
  };

  // Run (or re-run) the countdown from a given number of seconds. Wall-clock
  // based so it stays accurate across throttling; shared by start and resume.
  const runCountdown = useCallback((totalSeconds) => {
    clearTick();
    const endAt = Date.now() + totalSeconds * 1000;
    setSecondsLeft(totalSeconds);
    intervalRef.current = setInterval(() => {
      const left = Math.round((endAt - Date.now()) / 1000);
      if (left > 0) { setSecondsLeft(left); return; }
      clearTick();
      setSecondsLeft(null);
      const s = settingsRef.current || {};
      if (s.audioEnabled !== false) playSessionEndChime(s.audioVolume);
      if (s.completionHaptics !== false && s.hapticsEnabled !== false) {
        tapHaptic(); setTimeout(tapHaptic, 160); setTimeout(tapHaptic, 320);
      }
      // Pause tapping + stats until the user taps the prompt to continue.
      onSessionEnd();
      setAwaitingResume(true);
    }, 250);
  }, [settingsRef, onSessionEnd]);

  // minutes > 0 starts a countdown; pass 0 to cancel.
  const startTimer = useCallback((minutes) => {
    clearTick();
    setAwaitingResume(false);
    if (!minutes) { setSecondsLeft(null); return; }

    unlockAudio(); // called from a tap gesture, so the later chime can play
    runCountdown(minutes * 60);
  }, [runCountdown]);

  // Tap the "session complete" prompt to carry on: re-enable tapping + stats.
  const resumeFromSession = useCallback(() => {
    setAwaitingResume(false);
    onResume();
  }, [onResume]);

  // Full reset (e.g. on logout): cancel everything, clear the prompt.
  const resetTimer = useCallback(() => {
    clearTick();
    setSecondsLeft(null);
    setAwaitingResume(false);
  }, []);

  // Pause the countdown while `paused` (settings panel / modal open), then
  // resume from where it left off. Without this the wall-clock timer would
  // keep draining behind the panel.
  useEffect(() => {
    if (secondsLeftRef.current == null) return; // no active session
    if (paused) {
      clearTick();
    } else if (intervalRef.current == null) {
      runCountdown(secondsLeftRef.current);
    }
  }, [paused, runCountdown]);

  useEffect(() => () => clearTick(), []);

  return { secondsLeft, awaitingResume, startTimer, resumeFromSession, resetTimer };
};

export default useSessionTimer;
